// src/hooks/usePasskey.js
// ================================================================
// Drop this into src/hooks/usePasskey.js
//
// Install first:
//   npm install @simplewebauthn/browser
// ================================================================
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';
import api from '../api';

export { browserSupportsWebAuthn };

// ── Register a new passkey (must be called when user is logged in) ──
// password: the user's current account password (required by backend)
export async function registerPasskey(password) {
  // Step 1: get options from server
  const { data: startData } = await api.post('/auth/passkey/register/start', { password });
  if (!startData.success) throw new Error(startData.message);

  // Step 2: prompt browser / OS biometric
  let credential;
  try {
    credential = await startRegistration({ optionsJSON: startData.options });
  } catch (err) {
    if (err.name === 'InvalidStateError')
      throw new Error('A passkey for this device already exists on this account.');
    if (err.name === 'NotAllowedError')
      throw new Error('Passkey creation was cancelled.');
    throw err;
  }

  // Step 3: verify with server
  const { data: finishData } = await api.post('/auth/passkey/register/finish', { credential });
  if (!finishData.success) throw new Error(finishData.message);
  return finishData; // { device_name }
}

// ── Authenticate with a passkey (no JWT required) ──────────────────
// email: optional — pre-fills the allowCredentials list for faster UX
export async function loginWithPasskey(email = '') {
  // Step 1: get challenge options
  const { data: startData } = await api.post('/auth/passkey/login/start', { email });
  if (!startData.success) throw new Error(startData.message);

  // Step 2: prompt browser
  let credential;
  try {
    credential = await startAuthentication({ optionsJSON: startData.options });
  } catch (err) {
    if (err.name === 'NotAllowedError')
      throw new Error('Passkey sign-in was cancelled.');
    throw err;
  }

  // Step 3: verify and get JWT
  const { data: finishData } = await api.post('/auth/passkey/login/finish', { credential });
  if (!finishData.success) throw new Error(finishData.message);
  return finishData; // { token, user }
}

// ── List, delete, status ───────────────────────────────────────────
export const passkeyAPI = {
  list:         ()   => api.get('/auth/passkey/list'),
  remove:       (id) => api.delete(`/auth/passkey/${id}`),
  status:       ()   => api.get('/auth/passkey/status'),
  dismissNudge: ()   => api.post('/auth/passkey/dismiss-nudge'),
};